/**
 * Partea pura a lui `before_send` din PostHog: redactarea tokenului de colaborator. Separata de
 * `posthog.ts` ca sa se poata testa fara SDK si fara `window`.
 *
 * Tokenul sta in path (`/activitate/<token>`, `/api/collab/<token>`), deci ajunge in
 * `$current_url`, `$pathname`, `$referrer`, `$prev_pageview_pathname` si in proprietatile
 * autocapture. Iterarea e generica pe CAMPURI, ca la `envelope.ts` - acelasi pattern, alt SDK.
 */

import { redactCollabToken } from "@/shared/utils/redact-collab-token";

import { esteZgomotTertaDupaContinut } from "./envelope";

type Props = Record<string, unknown>;

function redactStrings(record: Props | undefined | null): void {
    if (!record) return;
    for (const key of Object.keys(record)) {
        const value = record[key];
        if (typeof value === "string") {
            record[key] = redactCollabToken(value);
        }
    }
}

/**
 * `$exception` care nu e a noastra: puntea nativa a WebView-ului Facebook si «Script error.» fara
 * cadru de pe originea noastra. Aceeasi regula ca pe App Insights (`esteZgomotTertaDupaContinut`) —
 * acolo zgomotul a disparut pe 08.10, aici a continuat: 142 din 153 de exceptii in 14 zile (13.10).
 */
function esteExceptieTerta(event: { event?: string; properties?: Props }, origine: string): boolean {
    if (event.event !== "$exception") return false;
    const lista = event.properties?.$exception_list;
    if (!Array.isArray(lista)) return false;

    const mesaje: string[] = [];
    const cadre: string[] = [];
    for (const exceptie of lista as Props[]) {
        if (!exceptie) continue;
        if (typeof exceptie.value === "string") mesaje.push(exceptie.value);
        const frames = (exceptie.stacktrace as { frames?: unknown } | undefined)?.frames;
        for (const cadru of (Array.isArray(frames) ? frames : []) as Props[]) {
            if (typeof cadru?.filename === "string") cadre.push(cadru.filename);
        }
    }
    return esteZgomotTertaDupaContinut(mesaje, cadre, origine);
}

/** Intoarce `null` cand evenimentul nu se trimite deloc (contractul lui `before_send`). */
export function sanitizePostHogEvent<T extends { event?: string; properties?: Props; $set?: Props; $set_once?: Props }>(
    event: T | null,
    origine: string = globalThis.location?.origin ?? "",
): T | null {
    if (!event) return event;
    if (esteExceptieTerta(event, origine)) return null;
    redactStrings(event.properties);
    redactStrings(event.$set);
    redactStrings(event.$set_once);
    return event;
}
